/**
 * A deliberately tiny markdown subset for assistant answers, parsed into a plain
 * tree that React renders as elements. There is no HTML anywhere in the path:
 * every string ends up as a React text node, so `<script>` or `<img onerror>` in
 * a model answer is displayed as text, never interpreted.
 *
 * Supported: paragraphs, `#` headings, `-`/`*`/`1.` lists, fenced code, and
 * inline **bold**, *italic*, `code` and [links](http(s) only).
 */

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'bold'; c: Inline[] }
  | { t: 'italic'; c: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string; c: Inline[] }
  | { t: 'br' };

export type Block =
  | { t: 'p'; c: Inline[] }
  | { t: 'heading'; c: Inline[] }
  | { t: 'list'; ordered: boolean; items: Inline[][] }
  | { t: 'code'; v: string };

/** The href if it is a plain http(s) URL, otherwise null. */
export function safeHttpUrl(href: string): string | null {
  try {
    const url = new URL(href.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

const INLINE = /\*\*(.+?)\*\*|`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)|\*([^*\s](?:[^*]*[^*\s])?)\*/;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  while (rest) {
    const m = INLINE.exec(rest);
    if (!m) {
      out.push({ t: 'text', v: rest });
      break;
    }
    if (m.index > 0) out.push({ t: 'text', v: rest.slice(0, m.index) });
    if (m[1] !== undefined) out.push({ t: 'bold', c: parseInline(m[1]) });
    else if (m[2] !== undefined) out.push({ t: 'code', v: m[2] });
    else if (m[3] !== undefined) {
      const href = safeHttpUrl(m[4]);
      // A non-http(s) target (javascript:, data:, mailto:…) keeps its label as plain text.
      out.push(href ? { t: 'link', href, c: parseInline(m[3]) } : { t: 'text', v: m[3] });
    } else out.push({ t: 'italic', c: parseInline(m[5]) });
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^#{1,6}\s+(.*)$/;
const FENCE = /^\s*```/;

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];

  const flushPara = () => {
    if (!para.length) return;
    const c: Inline[] = [];
    para.forEach((line, i) => {
      if (i > 0) c.push({ t: 'br' });
      c.push(...parseInline(line));
    });
    blocks.push({ t: 'p', c });
    para = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (FENCE.test(line)) {
      flushPara();
      const code: string[] = [];
      i++;
      while (i < lines.length && !FENCE.test(lines[i])) code.push(lines[i++]);
      blocks.push({ t: 'code', v: code.join('\n') });
      continue;
    }
    if (!line.trim()) {
      flushPara();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flushPara();
      blocks.push({ t: 'heading', c: parseInline(heading[1]) });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      flushPara();
      const ordered = !bullet;
      const last = blocks[blocks.length - 1];
      if (last && last.t === 'list' && last.ordered === ordered) last.items.push(parseInline(item[1]));
      else blocks.push({ t: 'list', ordered, items: [parseInline(item[1])] });
      continue;
    }
    para.push(line);
  }
  flushPara();
  return blocks;
}
